import {createSlice} from '@reduxjs/toolkit'

// ─── Step definitions ─────────────────────────────────────────────────────────

export const STEPS = [
  {
    id: 'email-lookup',
    label: 'Email Lookup',
    subtitle: 'Check if the customer email exists',
  },
  // {
  //   id: 'transaction-type',
  //   label: 'Transaction Type',
  //   subtitle: 'No. of locations to process',
  // },
  {
    id: 'account-info-setup',
    label: 'Account Setup',
    subtitle: 'Parent-child location setup',
  },
  {
    id: 'catalog-selection',
    label: 'Catalog Selection',
    subtitle: 'Select plans and add-ons',
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    subtitle: 'Book the onboarding call',
  },
  {
    id: 'payment-details',
    label: 'Payment Details',
    subtitle: 'Review and submit payment',
  },
]

// ─── Initial state ────────────────────────────────────────────────────────────

const getInitialState = () => ({
  currentStep: 0,
  stepHistory: [], // stack of step indices for Back navigation
  completed: [], // array of completed step ids
  isAddChildEvent: false,
  viewResultsEmail: null,

  emailLookupState: {
    verifiedEmail: null,
    accountType: null,
    meta: null,
  },

  transactionTypeState: {
    selectedTransactionType: null, // 'single-location' | 'multiple-locations'
  },

  countrySelect: {
    selectedCountry: null,
    states: null,
  },

  accountInfoState: {
    parents: [],
    activeAccordion: null,
    initialized: false,
  },

  catalogState: {
    CCAccountInfo: null,
    all5PlanPrices: null,
    defaultPlanConfig: null,
  },
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

const markStepCompleted = state => {
  const stepId = STEPS[state.currentStep]?.id
  if (stepId && !state.completed.includes(stepId)) {
    state.completed.push(stepId)
  }
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const accountSetupSlice = createSlice({
  name: 'accountSetup',
  initialState: getInitialState(),

  reducers: {
    // ─────────────────────────────────────────
    // Navigation
    // ─────────────────────────────────────────

    setStep(state, action) {
      state.currentStep = action.payload
      // Jumping to a step must exit the embedded "View Results" override,
      // otherwise the content area keeps rendering the stale AllAccounts/
      // MyAccounts view even though the Stepper shows the new step as active.
      state.viewResultsEmail = null
    },

    // Like goNext(), but jumps straight to an arbitrary step instead of
    // currentStep+1 — used by Catalog Selection's Continue to skip Scheduling
    // entirely when the cart doesn't need a kickoff call, while still marking
    // Catalog Selection completed (goNext() can't do this since Scheduling
    // isn't necessarily the very next step taken).
    completeCurrentAndSetStep(state, action) {
      markStepCompleted(state)
      state.stepHistory.push(state.currentStep)
      state.currentStep = action.payload
      state.viewResultsEmail = null
    },

    goNext(state) {
      markStepCompleted(state)

      state.stepHistory.push(state.currentStep)

      if (state.currentStep < STEPS.length - 1) {
        state.currentStep += 1
      }
    },

    goBack(state) {
      if (state.stepHistory.length > 0) {
        state.currentStep = state.stepHistory.pop()
      }
    },

    resetJourney() {
      return getInitialState()
    },

    // ─────────────────────────────────────────
    // Step States
    // ─────────────────────────────────────────

    setViewResultsEmail(state, action) {
      state.viewResultsEmail = action.payload ?? null
    },

    setEmailLookupState(state, action) {
      const {email, accountType, meta} = action.payload

      state.emailLookupState = {
        verifiedEmail: email,
        accountType,
        meta,
      }
    },

    setTransactionTypeState(state, action) {
      state.transactionTypeState.selectedTransactionType = action.payload
    },

    setCountrySelectState(state, action) {
      state.countrySelect.selectedCountry = action.payload
    },

    setSelectedCountryStates(state, action) {
      state.countrySelect.states = action.payload
    },

    clearStates(state) {
      state.countrySelect.states = []
    },

    // ─────────────────────────────────────────
    // Account Tree
    // ─────────────────────────────────────────

    initializeParents(state, action) {
      state.accountInfoState.parents = action.payload
      state.accountInfoState.initialized = true
    },

    setActiveAccordion(state, action) {
      state.accountInfoState.activeAccordion = action.payload
    },

    setParents(state, action) {
      state.accountInfoState.parents = action.payload
    },

    addChildAccount(state, action) {
      const {parentId, child} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      parent.children.push(child)
    },

    deleteChildAccount(state, action) {
      const {parentId, childId} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child || child.saved) return

      parent.children = parent.children.filter(c => c.id !== childId)
    },

    updateParentField(state, action) {
      const {parentId, field, value} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      parent.dirty = true

      parent.values[field] = value

      if (parent.errors) {
        delete parent.errors[field]
      }
    },

    updateChildField(state, action) {
      const {parentId, childId, field, value} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child) return

      child.dirty = true

      child.values[field] = value

      if (child.errors) {
        delete child.errors[field]
      }
    },

    setParentFieldErrors(state, action) {
      const {parentId, field, errors} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      if (errors[field]) {
        parent.errors[field] = errors[field]
      } else {
        delete parent.errors[field]
      }
    },

    setChildFieldErrors(state, action) {
      const {parentId, childId, field, errors} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child) return

      if (errors[field]) {
        child.errors[field] = errors[field]
      } else {
        delete child.errors[field]
      }
    },

    setParentErrors(state, action) {
      const {parentId, errors} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      parent.errors = errors
    },

    setChildErrors(state, action) {
      const {parentId, childId, errors} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child) return

      child.errors = errors
    },

    setParentSaving(state, action) {
      const {parentId, saving} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      parent.saving = saving
    },

    setChildSaving(state, action) {
      const {parentId, childId, saving} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child) return

      child.saving = saving
    },

    setParentSaved(state, action) {
      const {parentId, data} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      Object.assign(parent, data)
    },

    setChildSaved(state, action) {
      const {parentId, childId, data} = action.payload

      const parent = state.accountInfoState.parents.find(p => p.id === parentId)

      if (!parent) return

      const child = parent.children.find(c => c.id === childId)

      if (!child) return

      Object.assign(child, data)
    },

    setCatalogState(state, action) {
      state.catalogState = action.payload
    },

    updateCountryInTree(state, action) {
      const country = action.payload

      state.accountInfoState.parents.forEach(parent => {
        parent.country = country

        parent.values.country = country

        // clear address fields
        parent.values.address = ''
        parent.values.city = ''
        parent.values.state = ''
        parent.values.postCode = ''

        // clear errors too
        delete parent.errors.address
        delete parent.errors.city
        delete parent.errors.state
        delete parent.errors.postCode

        parent.children.forEach(child => {
          child.country = country

          child.values.country = country

          // clear address fields
          child.values.address = ''
          child.values.city = ''
          child.values.state = ''
          child.values.postCode = ''

          // clear errors too
          delete child.errors.address
          delete child.errors.city
          delete child.errors.state
          delete child.errors.postCode
        })
      })
    },
  },
})

// ─────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────

export const {
  setStep,
  completeCurrentAndSetStep,
  goNext,
  goBack,
  resetJourney,

  setViewResultsEmail,
  setEmailLookupState,
  setTransactionTypeState,

  setCountrySelectState,
  setSelectedCountryStates,
  clearStates,

  setAddChildEvent,

  initializeParents,
  setParents,
  setActiveAccordion,

  addParentAccount,
  addChildAccount,

  updateParentField,
  updateChildField,

  setParentFieldErrors,
  setChildFieldErrors,

  setParentErrors,
  setChildErrors,

  setParentSaving,
  setChildSaving,

  setParentSaved,
  setChildSaved,

  deleteChildAccount,

  updateCountryInTree,

  setCatalogState,
} = accountSetupSlice.actions

// ─────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────

const selectAccountSetup = state => state.accountSetup

export const selectCurrentStep = state => selectAccountSetup(state).currentStep

export const selectCompletedSteps = state => selectAccountSetup(state).completed

export const selectCurrentStepDef = state =>
  STEPS[selectAccountSetup(state).currentStep]

export const selectEmailLookupState = state =>
  selectAccountSetup(state).emailLookupState

export const selectTransactionTypeState = state =>
  selectAccountSetup(state).transactionTypeState

export const selectCountrySelectState = state =>
  selectAccountSetup(state).countrySelect

export const selectAddChildEvent = state =>
  selectAccountSetup(state).isAddChildEvent

export const selectAccountInfoState = state =>
  selectAccountSetup(state).accountInfoState

export const selectParents = state =>
  selectAccountSetup(state).accountInfoState.parents

export const selectActiveAccordion = state =>
  selectAccountSetup(state).accountInfoState.activeAccordion

export const selectViewResultsEmail = state =>
  selectAccountSetup(state).viewResultsEmail

export const selectCanGoBack = state =>
  selectAccountSetup(state).stepHistory.length > 0

export const selectCatalogState = state =>
  selectAccountSetup(state).catalogState

export const selectDefaultPlanConfig = state =>
  selectAccountSetup(state).catalogState?.defaultPlanConfig

// ── selectCanGoNext ──────────────────────────────────────────────────────────
// Each step defines its own "ready" condition.
// Key fix: use consistent field name selectedTransactionType (capital T).

export const selectCanGoNext = state => {
  const accSetup = selectAccountSetup(state)
  const step = STEPS[accSetup.currentStep]

  switch (step?.id) {
    case 'email-lookup':
      return Boolean(accSetup.emailLookupState.verifiedEmail)

    // case 'transaction-type':
    //   return Boolean(accSetup.transactionTypeState?.selectedTransactionType)

    case 'account-info-setup':
      return Boolean(accSetup.accountInfoState)

    case 'catalog-selection':
      return true

    case 'scheduling':
      return true

    case 'payment-details':
      return true

    default:
      return true
  }
}

export default accountSetupSlice.reducer
